import React, { useState } from 'react';
import { array, bool, func, oneOf, object, shape, string } from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import config from '../../config';
import facebookImage from '../../assets/freedomlySocialShare.png';
import twitterImage from '../../assets/freedomlySocialShare.png';
import {
  Page,
  LayoutWrapperFooter,
  Footer,
  SectionCategoryDescription,
  CategoryBanner,
  SectionChildCategories,
} from '../../components';
import { TopbarContainer } from '../../containers';

import { getListings, setActiveListing, setInitialValues } from '../../ducks/category.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { setSignUpFrom } from '../SignUpProjectPage/SignUpProjectPage.duck.js';

import css from './ParentCategoryPage.css';
import { propTypes } from '../../util/types';
import { parse, stringify } from '../../util/urlHelpers';
import MainPanel from '../SearchPage/MainPanel';
import {
  validURLParamsForExtendedData,
  pickSearchParamsOnly,
} from '../SearchPage/SearchPage.helpers';
import classNames from 'classnames';

const siteTitle = config.siteTitle;
const RESULT_PAGE_SIZE = 7;
const MODAL_BREAKPOINT = 962;

const ParentCategoryPageComponent = props => {
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);

  const {
    content,
    filterConfig,
    scrollingDisabled,
    onManageDisableScrolling,
    sortConfig,
    searchParams,
    onActivateListing,
    getListingsInProgress,
    location,
    intl,
    onSetSignUpFrom,
    currentUser,
    combinedReviews,
    getReviewsInProgress,
    skillOptions,
    toolOptions,
  } = props;

  const { mapSearch, page, ...searchInURL } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const schemaTitle = intl.formatMessage({ id: content.bannerTitleId }, { siteTitle });
  const schemaDescription = content.showDescription
    ? intl.formatMessage({
        id: `${content.bannerTitleId}PageSchemaDescription`,
      })
    : intl.formatMessage({ id: 'Page.schemaDescription' });
  const schemaImage = `${config.canonicalRootURL}${facebookImage}`;
  const pageUrl = `${config.canonicalRootURL}${location.pathname}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: siteTitle, item: config.canonicalRootURL },
      { '@type': 'ListItem', position: 2, name: schemaTitle, item: pageUrl },
    ],
  };

  // urlQueryParams doesn't contain page specific url params
  // like mapSearch, page or origin (origin depends on config.sortSearchByDistance)
  const urlQueryParams = pickSearchParamsOnly(searchInURL, filterConfig, sortConfig);

  // Page transition might initially use values from previous search
  const urlQueryString = stringify(urlQueryParams);
  const paramsQueryString = stringify(pickSearchParamsOnly(searchParams, filterConfig, sortConfig));
  const searchParamsAreInSync = urlQueryString === paramsQueryString;

  const validQueryParams = validURLParamsForExtendedData(searchInURL, filterConfig);

  // Set topbar class based on if a modal is open in
  // a child component
  const topbarClasses = isMobileModalOpen
    ? classNames(css.topbarBehindModal, css.topbar)
    : css.topbar;

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle + ' | Freedomly.io'}
      facebookImages={[
        { url: `${config.canonicalRootURL}${facebookImage}`, width: 1200, height: 630 },
      ]}
      twitterImages={[
        { url: `${config.canonicalRootURL}${twitterImage}`, width: 600, height: 314 },
      ]}
      schema={[
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          description: schemaDescription,
          name: schemaTitle,
          image: [schemaImage],
          url: pageUrl,
        },
        breadcrumbSchema,
      ]}
    >
      <TopbarContainer
        className={topbarClasses}
        currentSearchParams={urlQueryParams}
        currentPage="ParentCategoryPage"
      />
      <div className={css.container}>
        <div className={css.bannerContainer}>
          <CategoryBanner content={content} />
        </div>
        <SectionChildCategories categories={content.childCategories || []} />
        <MainPanel
          {...props}
          searchInProgress={getListingsInProgress}
          onActivateListing={onActivateListing}
          filterConfig={[filterConfig.find(x => x.id === 'filters')]}
          urlQueryParams={validQueryParams}
          onManageDisableScrolling={onManageDisableScrolling}
          searchParamsAreInSync={searchParamsAreInSync}
          searchParamsForPagination={parse(location.search)}
          showAsModalMaxWidth={MODAL_BREAKPOINT}
          onOpenModal={() => setIsMobileModalOpen(true)}
          onCloseModal={() => setIsMobileModalOpen(false)}
          onSetSignUpFrom={onSetSignUpFrom}
          onSetSignUpFromPath={location.pathname}
          currentUser={currentUser}
          currentPageReviews={combinedReviews}
          getReviewsInProgress={getReviewsInProgress}
          location={location}
          skillOptions={skillOptions}
          toolOptions={toolOptions}
        />
        <div className={css.descriptionContainer}>
          {content.showDescription && <SectionCategoryDescription content={content.seoContent} />}
        </div>
      </div>
      <LayoutWrapperFooter>
        <Footer />
      </LayoutWrapperFooter>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    getListingsInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    currentPageReviews,
    getReviewsInProgress,
  } = state.category;

  const pageListings = getListingsById(state, currentPageResultIds);

  const { skillOptions, toolOptions } = state.SkillsAndTools;

  return {
    currentUser,
    listings: pageListings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    getListingsInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    combinedReviews: currentPageReviews,
    getReviewsInProgress,
    skillOptions,
    toolOptions,
  };
};

ParentCategoryPageComponent.defaultProps = {
  listings: [],
  mapListings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  tab: 'listings',
  filterConfig: config.custom.filters,
  sortConfig: config.custom.sortConfig,
  activeListingId: null,
  routeName: null,
};

ParentCategoryPageComponent.propTypes = {
  listings: array,
  mapListings: array,
  onActivateListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,
  tab: oneOf(['filters', 'listings', 'map']).isRequired,
  filterConfig: propTypes.filterConfig,
  sortConfig: propTypes.sortConfig,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
  onSetSignUpFrom: signUpFrom => dispatch(setSignUpFrom(signUpFrom)),
});

const ParentCategoryPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ParentCategoryPageComponent);

ParentCategoryPage.setInitialValues = initialValues => setInitialValues(initialValues);
ParentCategoryPage.loadData = options => {
  const { search, category } = options;

  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const { page = 1, address, origin, ...rest } = queryParams;
  const originMaybe = config.sortSearchByDistance && origin ? { origin } : {};

  return getListings({
    ...rest,
    category,
    ...originMaybe,
    page,
    perPage: RESULT_PAGE_SIZE,
  });
};

export default ParentCategoryPage;
